import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { adminApiService } from "../services/api";

const useAddProduct = (toggleAddProduct, onProductAdded) => {
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      productHighlightedText: "",
      productLinks: [{ url: "", title: "" }],
    },
  });
  const [isFeatured, setIsFeatured] = useState(false);
  const [isBestSelling, setIsBestSelling] = useState(false);
  const [isPercentage, setIsPercentage] = useState(false);
  const [bannerImages, setBannerImages] = useState([]);
  const [productimages, setProductImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [colorImages, setColorImages] = useState({});
  const [specsField, setSpecsField] = useState([]);
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [specInputs, setSpecInputs] = useState({});
  const [loading, setLoading] = useState(false);

  const handlePercentageInput = () => setIsPercentage(!isPercentage);

  const handleColorImageChange = (e, color) => {
    const files = Array.from(e.target.files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setColorImages((prev) => ({
      ...prev,
      [color]: [...(prev[color] || []), ...files],
    }));
  };

  const handleRemoveColorImage = (color, index) => {
    setColorImages((prev) => ({
      ...prev,
      [color]: prev[color].filter((_, i) => i !== index),
    }));
  };

  const onSubmit = async (data) => {
    const formData = new FormData();

    // Append basic product information
    formData.append("name", data.productName);
    formData.append("description", data.productDescription);
    formData.append("sku", data.productSKU);
    formData.append("product_imei_list", data.product_imei_list);
    formData.append("category_id", data.productCategory);
    formData.append("price_customer", data.customerPrice);
    formData.append("use_percentage", isPercentage);
    formData.append("quantity", data.quantity);
    formData.append("product_discount", data.productDiscount);
    formData.append("isfeatured", isFeatured);
    formData.append("isbestselling", isBestSelling);

    // Append product links
    const productLinks =
      data.productLinks?.filter((link) => link.url && link.title) || [];
    formData.append("product_links", JSON.stringify(productLinks));

    // Append sizes and colors
    formData.append("sizes", sizes.join(","));
    formData.append("colors", colors.join(","));

    // Append color images
    colors.forEach((color) => {
      colorImages[color]?.forEach((image) => {
        formData.append(`color_images_${color}`, image.file);
      });
    });

    // Append percentage or fixed prices
    if (isPercentage) {
      formData.append("retailer_percentage", data.retailerPercentage);
      formData.append("distributor_percentage", data.distributorPercentage);
      formData.append("corporate_percentage", data.corporatePercentage);
    } else {
      formData.append("price_retailer", data.retailerPrice);
      formData.append("price_distributor", data.distributorPrice);
      formData.append("price_corporate", data.corporateClientPrice);
    }

    // Append product images
    productimages.forEach((image) => {
      if (image.file) {
        formData.append("product_images", image.file);
      }
    });

    // Append banner images
    bannerImages.forEach((image) => {
      if (image.file) {
        formData.append("banner_images", image.file);
      }
    });

    // Append specifications
    const specsArray = selectedSpecs.map((spec) => ({
      spec: spec,
      value: specInputs[spec] || "",
    }));

    formData.append("product_highlighted_text", data.productHighlightedText);

    formData.append("specifications", JSON.stringify(specsArray));

    // Log FormData contents for debugging
    // for (let [key, value] of formData.entries()) {
    //   console.log(key, value);
    // }

    setLoading(true);
    try {
      const response = await adminApiService.addProduct(formData);

      if (!response.ok) throw new Error("Network response was not ok");
      await response.json();
      toast.success("Product added successfully!");
      resetFormFields();
      toggleAddProduct();
      onProductAdded();
    } catch (error) {
      console.error(error);
      toast.error(`Failed to add product: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSpecsField = async () => {
    try {
      const response = await adminApiService.getProductSpecsFields();
      if (!response.ok) throw new Error("Failed to fetch specs field.");
      const data = await response.json();
      setSpecsField(data.columns);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApiService.getAllCategories();
      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    }
  };

  const handleImageChange = (e, setImage) => {
    const files = Array.from(e.target.files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImage((prevImages) => [...prevImages, ...files]);
  };

  const handleRemoveImage = (index, setImage, images) => {
    setImage(images.filter((_, i) => i !== index));
  };

  const resetFormFields = () => {
    reset();
    setProductImages([]);
    setBannerImages([]);
    setSizes([]);
    setColors([]);
    setColorImages({});
    setIsPercentage(false);
    setValue("productLinks", [{ url: "", title: "" }]);
  };

  const closingModal = () => {
    resetFormFields();
    toggleAddProduct();
  };

  const handleAddSpecification = (e) => {
    const selectedSpec = e.target.value;
    if (selectedSpec && !selectedSpecs.includes(selectedSpec)) {
      setSelectedSpecs([...selectedSpecs, selectedSpec]);
      setSpecInputs({ ...specInputs, [selectedSpec]: "" });
    }
  };

  const handleRemoveSpecification = (spec) => {
    setSelectedSpecs(selectedSpecs.filter((s) => s !== spec));
    const newSpecInputs = { ...specInputs };
    delete newSpecInputs[spec];
    setSpecInputs(newSpecInputs);
  };

  useEffect(() => {
    fetchCategories();
    fetchAllSpecsField();
  }, []);

  return {
    register,
    handleSubmit: handleSubmit(onSubmit),
    reset,
    setValue,
    watch,
    isFeatured,
    setIsFeatured,
    isPercentage,
    handlePercentageInput,
    bannerImages,
    setBannerImages,
    productimages,
    setProductImages,
    categories,
    sizes,
    setSizes,
    colors,
    setColors,
    colorImages,
    handleColorImageChange,
    handleRemoveColorImage,
    specsField,
    selectedSpecs,
    specInputs,
    handleAddSpecification,
    handleRemoveSpecification,
    handleImageChange,
    handleRemoveImage,
    closingModal,
    setSpecInputs,
    loading,
    isBestSelling,
    setIsBestSelling,
  };
};

export default useAddProduct;
